import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Dropdown, Flex, FloatButton, Menu, Spin } from 'antd';
import './Editor.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNotification } from '../../../Helper/NotificationProvider';
import { useBlocker, useLocation } from 'react-router-dom';
import { URL_IMAGES, URL_LABELS, URL_LOCATIONS } from '../../../service/endpoints';
import EditToolbar from './Toolbar/EditToolbar';
import { InfoCircleOutlined, LockOutlined, RedoOutlined, ThunderboltOutlined, UnlockOutlined } from '@ant-design/icons';
import EslToolbar from './Toolbar/EslToolbar';
import { v4 as uuidv4 } from 'uuid';
import { fetchLocationById, fetchLocations, patchLocation } from '../../../slices/locationSlice';
import { deleteData, getData, postImage, putData } from '../../../service/APIClient';
import { setCurrentRoute, setHasUnsavedEditorChanges } from '../../../slices/sidebarSlice';
import { consumeLeaveConfirmed } from '../../../Helper/navigationConfirm';
import {  ArrowBackOutlined} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { addMatching } from '../../../slices/matchingSlice';
import { fetchEvents, patchEvent } from '../../../slices/eventSlice';
import EditorPixiIntegration from './EditorPixiIntegration';


const initialState = {
  backgroundImage: null,
  width: 1000,
  height: 750,
  shapes: [],
  scanPaths: [],
  isLocked: false,
  history: [{ shapes: [], scanPaths: [] }],
  historyStep: 0,
  isDirty: false,
  rotation: 0,
};

const Editor = forwardRef((props, ref) => {
  const { openNotification, contextHolder } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [id, setId] = useState(props?.locationId || location.state.id || {});
  const [selectedIds, setSelectedIds] = useState([]);
  const fileInputRef = useRef(null);
  const collapsed = useSelector(state => state.sidebar.isCollapsed);
  const [color, setColor] = useState('#003eb3');
  const [borderWidth, setBorderWidth] = useState(window.innerWidth * 0.92);
  const [markerWidth, setMarkerWidth] = useState(50);
  const [markerHeight, setMarkerHeight] = useState(50);
  const [clipboard, setClipboard] = useState([]);
  const [gapX, setGapX] = useState(1);
  const [gapY, setGapY] = useState(1);
  const [gapAddX, setGapAddX] = useState(50);
  const [gapAddY, setGapAddY] = useState(50);
  const [maxElements, setMaxElements] = useState(3);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [numbering, setNumbering] = useState('row');
  const [number, setNumber] = useState(1);
  const [scanDirection, setScanDirection] = useState('standard');
  const [isManualSelecting, setIsManualSelecting] = useState(false);
  const [horizontalDirection, setHorizontalDirection] = useState('rtl');
  const [horizontalNumberingDirection, setHorizontalNumberingDirection] = useState('ltr');
  const [verticalNumberingDirection, setVerticalNumberingDirection] = useState('ttb');
  const shapeRefs = useRef({});
  const transformerRef = useRef();
  const selectionRectRef = useRef();
  const selectionLayerRef = useRef();
  const selectionStartPos = useRef({ x: 0, y: 0 });
  const stageRef = useRef();
  const lastInitializedRef = useRef({ id: null, blobId: null });
  const [rightClickedShapeId, setRightClickedShapeId] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [blockCounter, setBlockCounter] = useState("A")
  const currentLocation = useSelector(state => state.locations.currentLocation);
  const currentLocationStatus = useSelector(state => state.locations.currentLocationStatus);
  const [state, setState] = useState(initialState);
  const [bendMode, setBendMode] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const debugMode = useSelector((state) => state.sidebar.debugMode);
  const [bgLocked, setBgLocked] = useState(true);
  // Tracks whether a save is in flight — prevents the loading guard from
  // mounting the loading screen on top of the saving spinner.
  const isSavingRef = useRef(false);

  const {
    backgroundImage,
    width,
    height,
    shapes,
    scanPaths,
    isLocked,
    history,
    historyStep,
    isDirty,
  } = state;

  useImperativeHandle(ref, () => ({
    getSelectedIds: () => selectedIds,
  }));

  ////////////////// FETCH DATA /////////////////
  useEffect(() => {
    if (currentLocationStatus === 'idle' || (currentLocation && currentLocation._id !== id)) {
      // Show loading for 5 seconds when fetching new location
      if (currentLocation && currentLocation._id !== id) {
        setShowLoading(true);
        setTimeout(() => {
          setShowLoading(false);
        }, 2000);
      }
      dispatch(fetchLocationById(id));
    }
  }, [currentLocationStatus, dispatch, id]);

  // useEffect(() => {
  //     dispatch(fetchLocationById(id));
  // }, [id]);


  useEffect(() => {
    if (!currentLocation) return;

    const locId  = currentLocation._id;
    const blobId = currentLocation.backgroundImage?.blobId ?? null;

    // Skip full re-init when the same location is re-fetched (e.g. after save).
    // Only re-initialize when the location _id or background image blobId actually changes.
    if (locId === lastInitializedRef.current.id && blobId === lastInitializedRef.current.blobId) {
      return;
    }
    lastInitializedRef.current = { id: locId, blobId };

    const initialShapes    = currentLocation.layout?.map(item => item.shape)     || [];
    const initialScanPaths = currentLocation.scanPaths || [];

    if (currentLocation.blobUrl) {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.src = currentLocation.blobUrl;
      img.onload = () => {
        const w = currentLocation.backgroundImage.width  || img.naturalWidth;
        const h = currentLocation.backgroundImage.height || img.naturalHeight;

        setState({
          backgroundImage: img,
          width:  w,
          height: h,
          rotation: currentLocation.backgroundImage.rotation || 0,
          shapes:    initialShapes,
          scanPaths: initialScanPaths,
          isLocked:  currentLocation.locked,
          history:   [{ shapes: initialShapes, scanPaths: initialScanPaths }],
          historyStep: 0,
          isDirty:     true,
        });
      };
    } else {
      setState({
        ...initialState,
        shapes:      initialShapes,
        scanPaths:   initialScanPaths,
        isLocked:    currentLocation.locked,
        history:     [{ shapes: initialShapes, scanPaths: initialScanPaths }],
        historyStep: 0,
        isDirty:     true,
      });
    }
  }, [currentLocation, id]);

  ////////////////// UPLOAD /////////////////
  const handleImageUpload = async event => {
    const file = event.target.files[0];

    if (
      file &&
      (file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/svg+xml')
    ) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'location');
        formData.append('tags', 'editor');

        const postResponse = await postImage(URL_IMAGES, formData);
        const getResponse = await getData(`${URL_IMAGES}/${postResponse._id}`);

        // Stage the upload locally only — the location record (and the previous
        // image's blob) aren't touched until the user explicitly saves, so
        // exiting without saving leaves the server-side state untouched.
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.src = getResponse.url;
        img.onload = () => {
          setState(prev => ({
            ...prev,
            backgroundImage: img,
            width:   img.naturalWidth,
            height:  img.naturalHeight,
            pendingBackgroundImageChange: {
              fileName: postResponse.filename,
              blobId: getResponse.blobId,
              fileUrl: postResponse.url,
            },
            isDirty: true,
          }));
          setBgLocked(false);
        }
        openNotification(
          "bottomRight",
          "Hochgeladen",
          "Bild erfolgreich hochgeladen. Zum Übernehmen bitte speichern.",
          true
        );
      } catch (error) {
        console.error('Error uploading or retrieving image:', error);
        openNotification(
          'bottomRight',
          'Fehler',
          'Fehler beim Hochladen oder Laden der Bilder. Bitte später erneut versuchen!',
          false
        );
      }
    } else {
      openNotification(
        'bottomRight',
        'Fehler',
        'Falsches Bildformat (PNG, JPG, oder SVG).',
        false
      );
    }
  };


  ////////////////// SAVE /////////////////
  const save = useCallback(
    async (autosave = false, rotationOverride = state?.rotation) => {
      props.setIsSaving(true);
      isSavingRef.current = true;

      const layout = shapes.map(shape => ({
        chairId: shape.id,
        shape: shape,
        properties: shape.properties
      }));

      const blob = await stageRef.current.toBlob();
      const pendingImageChange = state.pendingBackgroundImageChange;

      try {
        if (currentLocation.backgroundImage.thumbnailBlobId) {
          try {
            await deleteData(`${URL_IMAGES}/blob/${currentLocation.backgroundImage.thumbnailBlobId}`);
          } catch (e) {
            // Ignore 404 — blob may already be gone; don't block the save
            if (e?.response?.status !== 404) console.error(e);
          }
        }

        // The main image was only staged locally (upload or delete) — the
        // previously saved blob was never removed, so clean it up now.
        if (pendingImageChange !== undefined && currentLocation.backgroundImage.blobId) {
          try {
            await deleteData(`${URL_IMAGES}/blob/${currentLocation.backgroundImage.blobId}`);
          } catch (e) {
            if (e?.response?.status !== 404) console.error(e);
          }
        }

        const nameShort = currentLocation.backgroundImage.fileName
          ? currentLocation.backgroundImage.fileName.slice(0, 10)
          : new Date().toISOString();
        const name = `${nameShort}_thumb`;
        const file = new File([blob], name, { type: blob.type });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'thumbnail');
        formData.append('tags', 'editor');

        const postResponse = await postImage(URL_IMAGES, formData);
        const getResponse = await getData(`${URL_IMAGES}/${postResponse._id}`);

        const effectiveRotation = rotationOverride ?? currentLocation.backgroundImage?.rotation ?? state.rotation ?? 0;
        const bgTransform = stageRef.current?.getBgTransform?.();

        // Resolve which image metadata to persist: a staged upload, a staged
        // deletion (empty), or — when nothing changed — the existing one.
        const imageMetaBase = pendingImageChange === 'delete'
          ? {}
          : pendingImageChange
            ? { fileName: pendingImageChange.fileName, blobId: pendingImageChange.blobId, fileUrl: pendingImageChange.fileUrl }
            : currentLocation.backgroundImage;

        await dispatch(
          patchLocation({
            locationId: id,
            updateData: {
              ...currentLocation,
              layout: layout,
              scanPaths: scanPaths,
              backgroundImage: pendingImageChange === 'delete'
                ? {}
                : {
                    ...imageMetaBase,
                    width: bgTransform?.w ?? width,
                    height: bgTransform?.h ?? height,
                    bgX: bgTransform?.x ?? 0,
                    bgY: bgTransform?.y ?? 0,
                    bgW: bgTransform?.w ?? width,
                    bgH: bgTransform?.h ?? height,
                    bgRot: bgTransform?.rot ?? 0,
                    rotation: effectiveRotation,
                    thumbnailBlobId: getResponse.blobId,
                    thumbnailUrl: getResponse.url
                  }
            }
          })
        ).unwrap();

        await dispatch(fetchLocationById(id)).unwrap();

        stageRef.current?.fitView?.();

        setState(prev => ({
          ...prev,
          isDirty: false,
          pendingBackgroundImageChange: undefined,
        }));

        props.setIsSaving(false);
        isSavingRef.current = false;

        openNotification(
          'bottomRight',
          'Gespeichert',
          'Änderungen wurden erfolgreich gespeichert',
          true
        );
      } catch (error) {
        props.setIsSaving(false);
        isSavingRef.current = false;
        openNotification(
          'bottomRight',
          'Fehler',
          'Änderungen konnten nicht gespeichert werden',
          false
        );
      }
    },
    [
      shapes,
      currentLocation,
      backgroundImage,
      width,
      height,
      isLocked,
      id,
      openNotification,
      scanPaths,
      state
    ]
  );

  ////////////////// DELETE /////////////////
  // Removing the image is staged locally only — the actual blob(s) and the
  // location record aren't touched until the user saves, so exiting without
  // saving leaves the previously saved image in place.
  const deleteImage = async () => {
    setState(prev => ({
      ...prev,
      backgroundImage: null,
      pendingBackgroundImageChange: 'delete',
      isDirty: true,
    }));
  }

  ////////////////// FLOAT MENU DATA /////////////////
  const menuItems = [
    {
      key: "details",
      label: "Details anzeigen",
      icon: <InfoCircleOutlined />,
      onClick: () => {
        setMenuVisible(false);
        props.setOpen(true)
      },
    },
    {
      key: "flash",
      label: "Label blinken",
      icon: <ThunderboltOutlined />,
      onClick: () => {
        setMenuVisible(false);
        putData(`${URL_LABELS}/flash`, {
          color: "RED",
          pattern: "FLASH_1_SECOND",
          duration: 4,
          objectIds: [
            (props.guestList.find((guest) => guest.chair === rightClickedShapeId)).matching.label
          ]
        });
      },
    },
    {
      key: "reset",
      label: "Stuhl zurücksetzen",
      icon: <RedoOutlined />,
      onClick: () => handleResetSingleChair(rightClickedShapeId),
    },
  ];

  ////////////////// FLOAT MENU /////////////////
  const menu = <Menu items={menuItems} />;

  ////////////////// ZOOM /////////////////
  const handleWheel = (e) => {
    if(e.evt.ctrlKey){
      e.evt.preventDefault(); // Prevent page scroll

      const stage = stageRef.current;
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();

      // Determine zoom direction
      const zoomAmount = e.evt.deltaY > 0 ? 1.1 : 0.9; // Scroll down to zoom out, up to zoom in
      const newScale = oldScale * zoomAmount;

      // Limit min/max zoom levels
      if (newScale < 0.5 || newScale > 5) return;

      // Adjust position to keep zoom centered around mouse pointer
      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };

      const newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };

      stage.scale({ x: newScale, y: newScale });
      stage.position(newPos);
      stage.batchDraw();
    }
  };

  ////////////////// RESET SINGLE CHAIR /////////////////
  const handleResetSingleChair = async (chairId) => {
    try {
      const updatedGuestList = props.guestList.map((guest) => {
        if (guest.chair === chairId) {
          return {
            ...guest,
            matching: {
              customFields: {},
              matched: false,
              label: "",
              template: ""
            },
          };
        }
        return guest;
      });
      await dispatch(patchEvent({
        eventId: props.event.key,
        changes: {
          guestList: updatedGuestList
        }
      }))

      const guest = props.guestList.find((guest) => guest.chair === chairId);
      if(guest && guest?.matching?.label){
        const labelData = {
          objectId: guest.matching.label,
          data: [
            {
              zoneId: 'template',
              value: "Default",
            },
            {
              zoneId: 'chair',
              value: '',
            },
            {
              zoneId: 'number',
              value: '',
            },
            {
              zoneId: 'name',
              value: '',
            },
            {
              zoneId: 'company',
              value: '',
            },
            {
              zoneId: 'message',
              value: '',
            }
          ]
        };
        await dispatch(addMatching({
          labels: [labelData],
          config: {
            params: {
              resetFields: true
            }
          }
        }));
      }

      openNotification(
        "bottomRight",
        "Stuhl zurückgesetzt",
        "Ausgewählter Stuhl wurde erfolgreich zurückgesetzt.",
        true
      );
      props.fetchEvent()
      props.setPatchedData(!props.patcheData);
    } catch (error) {
      console.error("Error resetting single chair:", error);
      openNotification(
        "bottomRight",
        "Fehler",
        "Stuhl konnte nicht zurückgesetzt werden. Bitte später erneut versuchen.",
        false
      );
    }
  };

  ////////////////// CONTEXT MENU /////////////////
  const handleShapeContextMenu = useCallback((shapeId, e) => {
    const clientX = e.evt.clientX;
    const clientY = e.evt.clientY;

    setRightClickedShapeId(shapeId);
    setMenuPosition({ x: clientX, y: clientY });
    setMenuVisible(true);
  }, []);

  // Close the context menu when clicking blank space / anywhere outside it.
  // The Dropdown uses trigger={[]}, so antd doesn't attach its own outside-click
  // handler — we add one here. Right-clicks are ignored (they re-open the menu).
  useEffect(() => {
    if (!menuVisible) return;
    const handlePointerDown = (e) => {
      if (e.button === 2) return; // right-click is handled by the contextmenu flow
      if (e.target.closest?.('.ant-dropdown')) return; // click inside the menu itself
      setMenuVisible(false);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [menuVisible]);

  ////////////////// BLOCKER /////////////////
  const blocker = useBlocker(!props.viewOnly && isDirty === true);


  ////////////////// USE_EFFECTS /////////////////
  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Some navigation triggers (e.g. the sidebar) already ask this exact
      // question themselves before calling navigate() — don't ask twice.
      if (consumeLeaveConfirmed()) {
        blocker.proceed();
        return;
      }

      const confirmLeave = window.confirm("Editor wird verlassen, vorgenommene Änderungen werden unter Umständen nicht gespeichert.");

      if (confirmLeave) {
        // User clicked "OK" -> allow navigation
        blocker.proceed();
      } else {
        dispatch(setCurrentRoute("locations"));
        blocker.reset();
      }
    }
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ""; // needed for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Let the sidebar know whether there are unsaved changes, so clicking a menu
  // entry can warn before navigating away instead of silently discarding them.
  useEffect(() => {
    dispatch(setHasUnsavedEditorChanges(!props.viewOnly && isDirty === true));
    return () => {
      dispatch(setHasUnsavedEditorChanges(false));
    };
  }, [dispatch, isDirty, props.viewOnly]);

  useEffect(() => {
    function handleResize() {
      setBorderWidth(window.innerWidth * 0.92);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width]);

  useEffect(() => {
    setState(prev => ({
      ...prev,
      isDirty: true,
    }));
  }, [width, height]);

  useEffect(() => {
    const nodes = [];
    selectedIds.forEach(id => {
      const node = shapeRefs.current[id];
      if (node) {
        nodes.push(node);
      }
    });
    if (transformerRef.current) {
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedIds, shapes]);


  ////////////////// API - GET LOCATION /////////////////
  const getLocation = async id => {
    return await getData(URL_LOCATIONS + '/' + id);
  };

  ////////////////// FIRSTMARKERS - MATCHING /////////////////
  const firstMarkers = useMemo(() => {
    const markers = new Set();
    scanPaths.forEach(path => {
      if (path.firstMarker) {
        markers.add(path.firstMarker);
      }
    });
    return markers;
  }, [scanPaths]);

  ////////////////// SCAN DIRECTION CHANGE /////////////////
  const handleScanDirectionChange = ((value, shapeIds = null) => {
    if (value === 'standard') {
      generateStandardScanPath(shapeIds);
      setIsManualSelecting(false);

      setState(prev => {
        const last = prev.scanPaths[prev.scanPaths.length - 1];
        const scanPaths =
          last && last.path.length === 0
            ? prev.scanPaths.slice(0, -1)
            : prev.scanPaths;
        return {
          ...prev,
          scanPaths
        };
      });
    } else {
      setIsManualSelecting(true);
      setState(prev => ({
        ...prev,
        scanPaths: [
          ...prev.scanPaths,
          { id: uuidv4(), path: [], firstMarker: null, currentMatchingMarker: null }
        ]
      }));
      openNotification(
        'bottomRight',
        'Manualler Modus',
        'Bitte Stühle in der gewünschten Reihenfolge ausählen.',
        true
      );
    }
  });

  ////////////////// SCAN DIRECTION CLEAR /////////////////
  const clearScanDirection = () => {
    if (selectedIds.length > 0) {
      const updatedScanPaths = scanPaths
        .map((scanPath) => ({
          ...scanPath,
          path: scanPath.path.filter((pathId) => !selectedIds.includes(pathId)),
        }))
        .filter((scanPath) => scanPath.path.length > 0);

      setState(prev => ({
        ...prev,
        scanPaths: updatedScanPaths
      }));
      updateHistory(shapes, updatedScanPaths);
      setSelectedIds([]);
    }
  };

  ////////////////// MATCHING MANUAL CHAIR SELECT /////////////////
  const handleManualSelection = shapeId => {
    if (!isManualSelecting) return;

    setState(prev => {
      const prevPaths = prev.scanPaths;
      const isAlreadyUsed = prevPaths.some(pathObj =>
        pathObj.path.includes(shapeId)
      );
      if (isAlreadyUsed) {
        openNotification(
          'bottomRight',
          'Doppelte Auswahl',
          'Dieser Stuhl ist bereits Teil einer anderen Scanrichtung',
          false
        );
        return prev;
      }

      const updatedPaths = [...prevPaths];
      const lastPath = updatedPaths[updatedPaths.length - 1];
      if (lastPath.path.length === 0) {
        lastPath.path.push(shapeId);
        lastPath.firstMarker = shapeId;
        lastPath.currentMatchingMarker = shapeId;
      } else {
        lastPath.path.push(shapeId);
      }

      updateHistory(prev.shapes, updatedPaths);
      return {
        ...prev,
        scanPaths: updatedPaths
      };
    });

  };

  ////////////////// SCAN PATH GENERATE /////////////////
  const generateStandardScanPath = (shapeIds) => {
    const usedShapeIds = scanPaths.flatMap(scanPath => scanPath.path);
    const availableShapes = shapeIds ? shapeIds : shapes.filter(
      shape =>
        selectedIds.includes(shape.id) && !usedShapeIds.includes(shape.id)
    );

    if (availableShapes.length === 0) {
      openNotification(
        'bottomRight',
        'Keine verfügbaren Stühle',
        'Alle ausgewählten Stühle sind bereits in Verwendung.',
        false
      );
      return;
    }
    const sortedByY = [...availableShapes].sort((a, b) => a.y - b.y);
    const rows = [];
    const rowThreshold = 10;

    sortedByY.forEach(shape => {
      const y = shape.y;
      let row = rows.find(r => Math.abs(r.y - y) <= rowThreshold);
      if (!row) {
        row = { y: y, shapes: [] };
        rows.push(row);
      }
      row.shapes.push(shape);
    });

    rows.forEach(row => {
      if (horizontalDirection === 'rtl') {
        row.shapes.sort((a, b) => b.x - a.x);
      } else {
        row.shapes.sort((a, b) => a.x - b.x);
      }
    });

    //rows.sort((a, b) => a.y - b.y);
    if (verticalNumberingDirection === 'btt') {
      rows.sort((a, b) => b.y - a.y); // bottom to top
    } else {
      rows.sort((a, b) => a.y - b.y); // top to bottom (default)
    }
    const path = [];
    rows.forEach((row, index) => {
      if (index % 2 === 0) {
        path.push(...row.shapes.map(shape => shape.id));
      } else {
        path.push(...row.shapes.map(shape => shape.id).reverse());
      }
    });

    if (path.length === 0) {
      openNotification(
        'bottomRight',
        'Keine Stühle hinzugefügt',
        'Keine Stühle wurden der Scanrichtung hinzugefügt.',
        false
      );
      return;
    }
    const firstMarker = path[0];
    const id = uuidv4()
    const currentMatchingMarker = path[0]
    const newScanPath = { id, path, firstMarker, currentMatchingMarker };
    const updatedScanPaths = [...scanPaths, newScanPath];
    setState(prev => ({
      ...prev,
      scanPaths: updatedScanPaths
    }));
    updateHistory(shapes, updatedScanPaths);
  };

  ////////////////// TRANSFORM END /////////////////
  const handleMultiTransformEnd = () => {
    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        const node = shapeRefs.current[shape.id];
        if (node) {
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          node.scaleX(1);
          node.scaleY(1);

          return {
            ...shape,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
            width: Math.max(5, shape.width * scaleX),
            height: Math.max(5, shape.height * scaleY)
          };
        }
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// TOOLBAR BTN CLICK /////////////////
  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  ////////////////// ADD CHAIR /////////////////
  const addShape = type => {
    const newShape = {
      x: 50,
      y: 100,
      fill: color,
      id: uuidv4(),
      type: type,
      ...(type === 'rectangle'
        ? { width: markerWidth, height: markerHeight }
        : { radius: 25 }),
      number: null,
      row: null,
      special: false
    };
    const updatedShapes = [...shapes, newShape];
    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// HISTORY - UNDO/REDO /////////////////
  const updateHistory = useCallback((newShapes, newScanPaths, explicitBgTransform) => {
    const newHistory = history.slice(0, historyStep + 1);
    // Snapshot bgTransform: use explicit value when provided (bg operations),
    // otherwise read from the stage ref (shape operations) so undo restores both.
    const bgT = explicitBgTransform !== undefined
      ? explicitBgTransform
      : (stageRef.current?.getBgTransform?.() ?? null);
    newHistory.push({ shapes: newShapes, scanPaths: newScanPaths, bgTransform: bgT });
    setState(prev => ({
      ...prev,
      history: newHistory,
      historyStep: newHistory.length - 1,
      isDirty: true
    }));
  }, [history, historyStep]);

  const handleUndo = () => {
    if (isLocked || props.viewOnly) return;
    if (historyStep > 0) {
      const prevStep = historyStep - 1;
      const prevState = history[prevStep];
      setState(prev => ({
        ...prev,
        shapes: prevState.shapes,
        scanPaths: prevState.scanPaths,
        historyStep: prevStep,
        isDirty: true
      }));
      updateShapeRefs(prevState.shapes);
      if (prevState.bgTransform !== undefined) {
        stageRef.current?.setBgTransformExternal?.(prevState.bgTransform);
      }
    }
  };

  const handleRedo = () => {
    if (isLocked || props.viewOnly) return;
    if (historyStep < history.length - 1) {
      const nextStep = historyStep + 1;
      const nextState = history[nextStep];
      setState(prev => ({
        ...prev,
        shapes: nextState.shapes,
        scanPaths: nextState.scanPaths,
        historyStep: nextStep,
        isDirty: true
      }));
      updateShapeRefs(nextState.shapes);
      if (nextState.bgTransform !== undefined) {
        stageRef.current?.setBgTransformExternal?.(nextState.bgTransform);
      }
    }
  };

  const updateShapeRefs = shapesArray => {
    const validIds = shapesArray.map(shape => shape.id);
    Object.keys(shapeRefs.current).forEach(id => {
      if (!validIds.includes(id)) {
        delete shapeRefs.current[id];
      }
    });
  };

  ////////////////// DELETE SELECTED /////////////////
  const deleteSelectedShapes = () => {
    if (isLocked || props.viewOnly) return;
    if (selectedIds.length > 0) {
      const updatedShapes = shapes.filter(
        shape => !selectedIds.includes(shape.id)
      );
      const updatedScanPaths = scanPaths
        .map((scanPath) => ({
          ...scanPath,
          path: scanPath.path.filter((pathId) => !selectedIds.includes(pathId)),
        }))
        .filter((scanPath) => scanPath.path.length > 0);

      setState(prev => ({
        ...prev,
        shapes: updatedShapes,
        scanPaths: updatedScanPaths
      }));
      updateHistory(updatedShapes, updatedScanPaths);
      selectedIds.forEach(id => {
        delete shapeRefs.current[id];
      });
      setSelectedIds([]);
    }
  };

  ////////////////// COLOR CHANGE /////////////////
  const colorChange = newColor => {
    setColor('#' + newColor.toHex());
    if (selectedIds.length > 0) {
      const updatedShapes = shapes.map(shape =>
        selectedIds.includes(shape.id)
          ? { ...shape, fill: '#' + newColor.toHex() }
          : shape
      );
      setState(prev => ({
        ...prev,
        shapes: updatedShapes
      }));
      updateHistory(updatedShapes, scanPaths);
    }
  };

  ////////////////// DUPLICATE SHAPES /////////////////
  const duplicateShapes = () => {
    if (selectedIds.length === 0 || isLocked || props.viewOnly) return;

    const newShapes = selectedIds
      .map(id => {
        const shapeToCopy = shapes.find(shape => shape.id === id);
        if (!shapeToCopy) return null;

        return {
          ...shapeToCopy,
          id: uuidv4(),
          x: shapeToCopy.x + 20,
          y: shapeToCopy.y + 20,
          matching: {
            label: "",
            template: "",
            matched: false,
            customFields: {}
          }
        };
      })
      .filter(Boolean);
    const updatedShapes = [...shapes, ...newShapes];

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    setSelectedIds(newShapes.map(shape => shape.id));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// MOUSE EVENTS - DOWN/MOVE/UP /////////////////
  const handleMouseDown = (e) => {

    if (isLocked) return;
    if (e.evt.button !== 2) return;

    const stage = e.target.getStage();
    if (e.target !== stage) return;

    e.evt.preventDefault();

    const p = stage.getPointerPosition();
    const start = {
      x: (p.x - stage.x()) / stage.scaleX(),
      y: (p.y - stage.y()) / stage.scaleY()
    };
    selectionStartPos.current = start;
    const rect = selectionRectRef.current;
    rect.visible(true);
    rect.x(start.x);
    rect.y(start.y);
    rect.width(0);
    rect.height(0);

    selectionLayerRef.current.batchDraw();
  };


  const handleMouseMove = (e) => {
    if (isLocked ) return;
    const rect = selectionRectRef.current;
    if (!rect.visible()) return;
    const pointer  = e.target.getStage().getPointerPosition();
    const start = selectionStartPos.current;
    const newX = (pointer.x - e.target.getStage().x()) / e.target.getStage().scaleX();
    const newY = (pointer.y - e.target.getStage().y()) / e.target.getStage().scaleY();

    rect.visible(true);
    rect.x(Math.min(newX, start.x));
    rect.y(Math.min(newY, start.y));
    rect.width(Math.abs(newX - start.x));
    rect.height(Math.abs(newY - start.y));
    selectionLayerRef.current.batchDraw();
  };

  const handleMouseUp = () => {
    if (isLocked) return;

    const rect = selectionRectRef.current;
    if (!rect.visible()) return;

    const stage = stageRef.current;
    const x      = rect.x() * stage.scaleX() + stage.x();
    const y      = rect.y() * stage.scaleY() + stage.y();
    const width  = rect.width()  * stage.scaleX();
    const height = rect.height() * stage.scaleY();

    const selected = shapes
      .map(shape => {
        const node = shapeRefs.current[shape.id];
        if (!node) return null;
        const box = node.getClientRect();
        return (
          box.x <  x+width  &&
          box.x+box.width > x     &&
          box.y <  y+height &&
          box.y+box.height > y
        )
          ? shape.id
          : null;
      })
      .filter(Boolean);

    setSelectedIds(selected);
    rect.visible(false);
    selectionLayerRef.current.batchDraw();
  };

  ////////////////// CUT/COPY/PASTE SHAPES /////////////////
  const cutShapes = () => {
    if (selectedIds.length === 0 || isLocked || props.viewOnly) return;
    const shapesToCut = shapes.filter(shape => selectedIds.includes(shape.id));
    setClipboard(shapesToCut);

    const remainingShapes = shapes.filter(
      shape => !selectedIds.includes(shape.id)
    );

    setState(prev => ({
      ...prev,
      shapes: remainingShapes
    }));
    updateHistory(remainingShapes, scanPaths);
    selectedIds.forEach(id => {
      delete shapeRefs.current[id];
    });

    setSelectedIds([]);
  };

  const copyShapes = () => {
    if (selectedIds.length === 0 || isLocked || props.viewOnly) return;

    const shapesToCopy = shapes.filter(shape => selectedIds.includes(shape.id));
    setClipboard(shapesToCopy);
  };

  const pasteShapes = () => {
    if (clipboard.length === 0 || isLocked || props.viewOnly) return;

    const newShapes = clipboard.map(shape => {
      return {
        ...shape,
        id: uuidv4(),
        x: shape.x + 20,
        y: shape.y + 20,
        matching: {
          label: "",
          template: "",
          matched: false,
          customFields: {}
        }
      };
    });

    const updatedShapes = [...shapes, ...newShapes];
    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);

    setSelectedIds(newShapes.map(shape => shape.id));
  };

  ////////////////// SHORTCUTS KEYS /////////////////
  useEffect(() => {
    const handleKeyDown = event => {
      if(props.viewOnly)
        return
      if (event.ctrlKey && event.key === 'z') {
        event.preventDefault();
        handleUndo();
      } else if (event.ctrlKey && event.key === 'y') {
        event.preventDefault();
        handleRedo();
      } else if (event.ctrlKey && event.key === 'd') {
        event.preventDefault();
        duplicateShapes();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        if (stageRef.current?.isBgSelected?.()) {
          deleteImage();
        } else {
          deleteSelectedShapes();
        }
      } else if (event.ctrlKey && event.key === 'x') {
        event.preventDefault();
        cutShapes();
      } else if (event.ctrlKey && event.key === 'c') {
        event.preventDefault();
        copyShapes();
      } else if (event.ctrlKey && event.key === 'v') {
        event.preventDefault();
        pasteShapes();
      } else if (event.ctrlKey && event.key === 'a') {
        event.preventDefault();
        markAll();
      } else if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        save();
      } else if (event.ctrlKey) {
        //
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handleUndo,
    handleRedo,
    copyShapes,
    deleteSelectedShapes,
    deleteImage,
    cutShapes,
    pasteShapes,
    duplicateShapes
  ]);

  const toggleSelectId = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  ////////////////// MIRROR SHAPES /////////////////
  const mirrorSelection = () => {
    if (selectedIds.length === 0) return; // No selection

    const selectedShapes = selectedIds.map(id => shapes.find(shape => shape.id === id)).filter(Boolean);
    if (selectedShapes.length === 0) return;

    const minX = Math.min(...selectedShapes.map(shape => shape.x));
    const maxX = Math.max(...selectedShapes.map(shape => shape.x));
    const selectionCenterX = (minX + maxX) / 2;

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return {
          ...shape,
          x: selectionCenterX - (shape.x - selectionCenterX),
          scaleX: -1,
          textScaleX: 1,
        };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// ROTATE SHAPES /////////////////
  const rotateSelection = (direction = "right") => {
    if (selectedIds.length === 0) return;

    const selectedShapes = selectedIds.map(id => shapes.find(shape => shape.id === id)).filter(Boolean);
    if (selectedShapes.length === 0) return;

    const minX = Math.min(...selectedShapes.map(shape => shape.x));
    const maxX = Math.max(...selectedShapes.map(shape => shape.x));
    const minY = Math.min(...selectedShapes.map(shape => shape.y));
    const maxY = Math.max(...selectedShapes.map(shape => shape.y));

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const rotateClockwise = direction === "right";

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        const offsetX = shape.x - centerX;
        const offsetY = shape.y - centerY;

        return {
          ...shape,
          x: rotateClockwise ? centerX + offsetY : centerX - offsetY,
          y: rotateClockwise ? centerY - offsetX : centerY + offsetX,
          rotation: (shape.rotation || 0) + (rotateClockwise ? 90 : -90),
          scaleX: 1,
          scaleY: 1,
          textRotation: 0,
        };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// CENTER SHAPES /////////////////
  const centerShapesVertically = () => {
    if (selectedIds.length === 0) return;

    const avgX =
      selectedIds
        .map(id => shapes.find(shape => shape.id === id)?.x || 0)
        .reduce((sum, x) => sum + x, 0) / selectedIds.length;

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return {
          ...shape,
          x:
            avgX - (shape.type === 'rectangle' ? shape.width / 2 : shape.radius)
        };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  const centerShapesHorizontally = () => {
    if (selectedIds.length === 0) return;

    const avgY =
      selectedIds
        .map(id => shapes.find(shape => shape.id === id)?.y || 0)
        .reduce((sum, y) => sum + y, 0) / selectedIds.length;

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return {
          ...shape,
          y: avgY - (shape.type === 'rectangle' ? shape.height / 2 : shape.radius),
        };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  const initialWidthRef = useRef(null);
  const initialHeightRef = useRef(null);

  ////////////////// RESET WIDTH/GAP /////////////////
  useEffect(() => {
    initialWidthRef.current = null;
    initialHeightRef.current = null;
    setGapX(1)
    setGapY(1)
  }, [selectedIds]);

  ////////////////// SCALE SHAPES /////////////////
  const scaleShapesInX = (gapX) => {

    if (selectedIds.length < 2) {
      return shapes;
    }
    const selectedShapes = shapes.filter(s => selectedIds.includes(s.id));
    const shapeCount = selectedShapes.length;
    const intervals = shapeCount - 1;

    const minX = Math.min(...selectedShapes.map(s => s.x));
    const maxX = Math.max(...selectedShapes.map(s => s.x));
    const currentWidth = maxX - minX;

    if (initialWidthRef.current === null) {
      initialWidthRef.current = currentWidth;
    }

    const desiredWidth = gapX === 1 ? initialWidthRef.current :
      (gapX * intervals < initialWidthRef.current ? initialWidthRef.current + gapX : gapX * intervals);

    let scaleX = 1;
    if (currentWidth > 0) {
      scaleX = desiredWidth / currentWidth;
    }

    const updatedShapes = shapes.map(shape => {
      if (!selectedIds.includes(shape.id)) {
        return shape;
      }

      const offsetX = shape.x - minX;
      const newX = Math.round(minX + offsetX * scaleX);
      return {
        ...shape,
        x: newX,
      };
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };


  const scaleShapesInY = (gapY) => {
    if (selectedIds.length < 2) {
      return shapes;
    }

    const selectedShapes = shapes.filter(s => selectedIds.includes(s.id));
    const shapeCount = selectedShapes.length;
    const intervals = shapeCount - 1;

    const minY = Math.min(...selectedShapes.map(s => s.y));
    const maxY = Math.max(...selectedShapes.map(s => s.y));

    const currentHeight = maxY - minY;

    if (initialHeightRef.current === null) {
      initialHeightRef.current = currentHeight;
    }

    const desiredHeight = gapY === 1 ? initialHeightRef.current :
      (gapY * intervals < initialHeightRef.current ? initialHeightRef.current + gapY : gapY * intervals);

    let scaleY = 1;
    if (currentHeight > 0) {
      scaleY = desiredHeight / currentHeight;
    }

    const updatedShapes = shapes.map(shape => {
      if (!selectedIds.includes(shape.id)) {
        return shape;
      }
      const offsetY = shape.y - minY;
      const newY = Math.round(minY + offsetY * scaleY);
      return {
        ...shape,
        y: newY,
      };
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  ////////////////// ALIGN SHAPES GRID /////////////////
  const alignShapesInGrid = () => {
    if (selectedIds.length === 0) return;

    const selectedShapes = shapes.filter(shape => selectedIds.includes(shape.id));
    const shapeCount = selectedShapes.length;

    if (shapeCount < 2) {
      return;
    }

    const minY = Math.min(...selectedShapes.map(s => s.y));
    const minX = Math.min(...selectedShapes.map(s => s.x));

    // Detect the number of columns by clustering shapes by x-position instead
    // of counting the top row: if the selection isn't perfectly aligned yet
    // (the whole reason to run "align in grid"), a jittered top row can look
    // shorter than it visually is and collapse the whole grid into too few
    // columns. Columns stay consistent even when rows are jittered.
    const columns = groupShapesIntoCols(selectedShapes, selectedShapes[0].width * 1.25);
    const maxElements = columns.length || 1;

    const sortedShapes = [...selectedShapes].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    const updatedShapes = [...shapes];

    sortedShapes.forEach((shape, i) => {
      const row = Math.floor(i / maxElements);
      const col = i % maxElements;

      const newY = minY + row * (selectedShapes[0].height * 2);
      const newX = minX + col * (selectedShapes[0].width * 2);

      const shapeIndex = updatedShapes.findIndex(s => s.id === shape.id);
      updatedShapes[shapeIndex] = {
        ...updatedShapes[shapeIndex],
        x: newX,
        y: newY,
      };
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };


  ////////////////// ADD SHAPES GRID /////////////////
  const addItemsInGrid = () => {
    if (rows <= 0 || cols <= 0) return;
    const newShapes = [];

    const startX = 75;
    const startY = 75;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = startX + col * (50 + gapAddX);
        const y = startY + row * (50 + gapAddY);
        const newShape = {
          x: x,
          y: y,
          width: 50,
          height: 50,
          id: uuidv4(),
          fill: color,
          type: 'rectangle',
          block: ''
        };
        newShapes.push(newShape);
      }
    }
    const updatedShapes = [...shapes, ...newShapes];
    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    setSelectedIds(newShapes.map(shape => shape.id));
    updateHistory(updatedShapes, scanPaths);
  };

  const markAll = () => {
    const allShapeIds = shapes.map(shape => shape.id);
    setSelectedIds(allShapeIds);
  };

  const toggleLock = () => {
    setState(prev => ({
      ...prev,
      isLocked: !prev.isLocked
    }));
  };

  ////////////////// NUMBER SEATS ROW/BLOCK /////////////////
  const groupShapesIntoRows = (shapes, threshold) => {
    const sortedShapes = [...shapes].sort((a, b) => a.y - b.y);
    const rows = [];
    sortedShapes.forEach(shape => {
      const row = rows.find(r => Math.abs(r.y - shape.y) <= threshold);

      if (row) {
        row.shapes.push(shape);
      } else {
        rows.push({ y: shape.y, shapes: [shape] });
      }
    });

    return rows;
  }

  const groupShapesIntoCols = (shapes, threshold) => {
    const sortedShapes = [...shapes].sort((a, b) => a.x - b.x);
    const cols = [];
    sortedShapes.forEach(shape => {
      const col = cols.find(c => Math.abs(c.x - shape.x) <= threshold);

      if (col) {
        col.shapes.push(shape);
      } else {
        cols.push({
          x: shape.x,
          shapes: [shape]
        });
      }
    });

    return cols;
  };

  const numberSeatsPerRow = (startNumber = null, custom = false, customShapes = null) => {
    const selectedShapes = customShapes ? customShapes : shapes.filter(shape =>
      selectedIds.includes(shape.id)
    );

    if (selectedShapes.length === 0) return;

    const rows = groupShapesIntoRows(selectedShapes, markerHeight / 4);
    const updatedShapes = customShapes ? [...shapes, ...customShapes]
      : shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return { ...shape, number: null, row: null };
      }
      return shape;
    });

    if (verticalNumberingDirection === 'btt') {
      rows.sort((a, b) => b.y - a.y);
    } else {
      rows.sort((a, b) => a.y - b.y);
    }

    rows.forEach((row, rowIndex) => {
      let sortedRowShapes;
      if (horizontalNumberingDirection === 'rtl') {
        sortedRowShapes = [...row.shapes].sort((a, b) => b.x - a.x);
      } else {
        sortedRowShapes = [...row.shapes].sort((a, b) => a.x - b.x);
      }

      sortedRowShapes.forEach((shape, index) => {
        const shapeIndex = updatedShapes.findIndex(s => s.id === shape.id);
        if (shapeIndex !== -1) {
          updatedShapes[shapeIndex].number =
            startNumber && !custom
              ? startNumber + index
              : custom
                ? startNumber
                : index + 1;
          updatedShapes[shapeIndex].row = rowIndex + 1;
        }
      });
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  }

  const numberSeatsPerBlock = (startNumber = null, custom = false) => {
    const selectedShapes = shapes.filter(shape =>
      selectedIds.includes(shape.id)
    );

    if (selectedShapes.length === 0) return;

    const rowGroups = groupShapesIntoRows(selectedShapes, markerHeight / 4);

    // Apply vertical direction (top-to-bottom or bottom-to-top)
    if (verticalNumberingDirection === 'btt') {
      rowGroups.sort((a, b) => b.y - a.y);
    } else {
      rowGroups.sort((a, b) => a.y - b.y);
    }

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return { ...shape, number: null, row: null };
      }
      return shape;
    });

    let seatCounter;
    if (custom && startNumber !== null) {
      seatCounter = startNumber;
    } else if (startNumber !== null && !custom) {
      seatCounter = startNumber;
    } else {
      seatCounter = 1;
    }

    rowGroups.forEach((row, rowIndex) => {
      // Apply horizontal direction (ltr or rtl)
      let sortedRowShapes;
      if (horizontalNumberingDirection === 'rtl') {
        sortedRowShapes = [...row.shapes].sort((a, b) => b.x - a.x);
      } else {
        sortedRowShapes = [...row.shapes].sort((a, b) => a.x - b.x);
      }

      sortedRowShapes.forEach((shape) => {
        const shapeIndex = updatedShapes.findIndex(s => s.id === shape.id);
        if (shapeIndex !== -1) {
          updatedShapes[shapeIndex].number = seatCounter;
          updatedShapes[shapeIndex].row = rowIndex + 1;
          seatCounter += 1;
        }
      });
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  const clearNumbering =() => {
    if (selectedIds.length === 0) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Keine Stühle ausgewählt.',
        false
      );
      return;
    }

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return { ...shape, number: null, row: null };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  }

  ////////////////// BLOCK /////////////////
  const setBlocks = (block) => {
    if (selectedIds.length === 0) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Keine Stühle ausgewählt.',
        false
      );
      return;
    }

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        return { ...shape, block: block };
      }
      return shape;
    });

    if(block != null)
      setBlockCounter(String.fromCharCode(block.charCodeAt(0) + 1))

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  }

  ////////////////// BEND SHAPES /////////////////
  const curveValues = useMemo(() => {
    const selectedShapes = shapes.filter(shape => selectedIds.includes(shape.id));

    const minX = Math.min(...selectedShapes.map(shape => shape.x));
    const minY = Math.min(...selectedShapes.map(shape => shape.y));
    const maxX = Math.max(...selectedShapes.map(shape => shape.x));
    const maxY = Math.max(...selectedShapes.map(shape => shape.y));

    return {
      "minX": minX,
      "minY": minY,
      "maxX": maxX,
      "maxY": maxY,
      "width" : maxX - minX,
      "height" : maxY - minY,
    }

  },[selectedIds])

  const handleStraightenShapes = () => {
    if (isLocked || props.viewOnly) return;
    if (selectedIds.length === 0) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Keine Stühle mit Biegung ausgewählt.',
        false
      );
      return;
    }

    const spacing = 75;
    const startX = curveValues.minX;
    const startY = curveValues.maxY;

    const selectedShapes = shapes.filter(shape => selectedIds.includes(shape.id));
    const sortedShapes = selectedShapes.sort((a, b) => a.x - b.x);

    const updatedShapes = shapes.map(shape => {
      if (selectedIds.includes(shape.id)) {
        const index = sortedShapes.findIndex(s => s.id === shape.id);
        return {
          ...shape,
          x: startX + index * spacing,
          y: startY,
          rotation: 0,
        };
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  }

  ////////////////// SPECIAL SHAPES /////////////////
  const markAsSpecial = () => {
    if (isLocked || props.viewOnly) return; // Prevent action if the editor is locked
    if (selectedIds.length === 0) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Keine Stühle ausgewählt.',
        false
      );
      return;
    }

    const updatedShapes = shapes.map((shape) => {
      if (selectedIds.includes(shape.id)) {
        return { ...shape, special: !shape.special }; // Toggle 'special' field
      }
      return shape;
    });

    setState(prev => ({
      ...prev,
      shapes: updatedShapes
    }));
    updateHistory(updatedShapes, scanPaths);
  };

  const handleApplyBend = useCallback((updatedShapes) => {
    const merged = shapes.map(s => updatedShapes.find(u => u.id === s.id) || s);
    setState(prev => ({ ...prev, shapes: merged }));
    updateHistory(merged, scanPaths);
  }, [shapes, scanPaths, updateHistory]);

  ////////////////// HANDLE SELECT /////////////////
  const handleSelect = useCallback(
    (id) => {
      if (isManualSelecting) {
        handleManualSelection(id);
      } else {
        setSelectedIds([id]);
        if (props.onShapeSelect) {
          props.onShapeSelect(id);
        }
      }
    },
    [isManualSelecting, handleManualSelection]
  );

  ////////////////// HANDLE CHANGE /////////////////
  const handleChange = useCallback(newAttrs => {
    setState(prev => {
      const prevShapes = prev.shapes
      const updated = prevShapes.map(s =>
        s.id === newAttrs.id ? newAttrs : s
      );
      updateHistory(updated, scanPaths);
      return ({
        ...prev,
        shapes: updated
      })
    });
  }, [scanPaths, updateHistory]);

  // Don't render Editor if we have old location data (willShowOldData = true) or if loading
  if (!isSavingRef.current && ((currentLocation && currentLocation._id !== id) || showLoading || currentLocationStatus === 'loading' || !currentLocation)) {
    return (
      <>
        {contextHolder}
        <Flex vertical justify="center" align="center" style={{ height: '100vh' }}>
          <Spin size="large" />
          <div style={{ marginTop: '12px', color: '#888', fontSize: '14px' }}>Daten werden geladen…</div>
        </Flex>
      </>
    );
  }

  // Show full-screen loading overlay when navigating back
  if (isNavigatingBack) {
    return (
      <>
        {contextHolder}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Flex vertical justify="center" align="center">
            <Spin size="large" />
          </Flex>
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <Flex vertical>
        {menuVisible && props.viewOnly && (
          <div
            // Remount on position change so the popup re-aligns when right-clicking
            // another chair while the menu is already open.
            key={`${menuPosition.x},${menuPosition.y}`}
            style={{
              position: "absolute",
              top: menuPosition.y,
              left: menuPosition.x,
              zIndex: 999,
            }}
          >
            <Dropdown
              overlay={menu}
              open
              trigger={[]}
              onOpenChange={(visible) => {
                if (!visible) setMenuVisible(false);
              }}
            >
              {/* an invisible <div> that triggers the actual dropdown */}
              <div style={{ width: 1, height: 1 }} />
            </Dropdown>
          </div>
        )}
        <input
          type="file"
          accept="image/png, image/jpeg, image/svg+xml"
          ref={fileInputRef}
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
        <div
          onContextMenu={(e) => e.preventDefault()} // Prevent default right-click menu
        >
        <EditorPixiIntegration
          currentLocation={currentLocation}
          props={props}
          state={state}
          setState={setState}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          stageRef={stageRef}
          bendMode={bendMode}
          onApplyBend={handleApplyBend}
          onUpdateHistory={updateHistory}
          onContextMenu={handleShapeContextMenu}
          isManualSelecting={isManualSelecting}
          onManualSelect={handleManualSelection}
          debugMode={debugMode}
          bgLocked={bgLocked}
        />
        </div>
          <FloatButton
            icon={<ArrowBackOutlined />}
            className="lock-btn"
            shape="square"
            onClick={async () => {
              // Check before unmounting anything: setIsNavigatingBack(true) below
              // tears down the whole PixiJS editor tree (it swaps in a full-screen
              // spinner), which would drop any in-progress/uncommitted background
              // image position. Confirm first so staying on "cancel" leaves it intact.
              if (!props.viewOnly && isDirty) {
                const confirmLeave = window.confirm("Editor wird verlassen, vorgenommene Änderungen werden unter Umständen nicht gespeichert.");
                if (!confirmLeave) {
                  return;
                }
                // Already confirmed above — clear isDirty so the router's own
                // useBlocker (which also watches isDirty) doesn't ask a second time.
                setState(prev => ({ ...prev, isDirty: false }));
              }
              setIsNavigatingBack(true);
              try {
                if (props.viewOnly){
                  await dispatch(fetchLocations())
                  await dispatch(fetchEvents())
                  navigate('/events');
                }
                else {
                  await dispatch(fetchLocations())
                  await dispatch(fetchEvents())
                  navigate('/locations');
                }
              } finally {
                setIsNavigatingBack(false);
              }
            }}
            style={{
              marginTop: '7.5px',
              marginLeft: collapsed ? '35px' : '175px',
              transition: 'margin-left 0.25s ease'
            }}
          />
          {(!props.viewOnly && <FloatButton
            icon={
              isLocked ? (
                <LockOutlined style={{ color: 'red' }} />
              ) : (
                <UnlockOutlined style={{ color: 'green' }} />
              )
            }
            className="lock-btn"
            shape="square"
            onClick={toggleLock}
            style={{
              marginTop: '7.5px',
              marginLeft: collapsed ? '80px' : '220px',
              transition: 'margin-left 0.25s ease'
            }}
          />)}

          {!isLocked && !props.viewOnly && (
            <EditToolbar
              width={width}
              height={height}
              isLocked={isLocked}
              gapX={gapX}
              setGapX={setGapX}
              gapY={gapY}
              setGapY={setGapY}
              gapAddX={gapAddX}
              setGapAddX={setGapAddX}
              gapAddY={gapAddY}
              setGapAddY={setGapAddY}
              rows={rows}
              setRows={setRows}
              cols={cols}
              setCols={setCols}
              maxElements={maxElements}
              setMaxElements={setMaxElements}
              setState={setState}
              setMarkerHeight={setMarkerHeight}
              setMarkerWidth={setMarkerWidth}
              addShape={addShape}
              handleButtonClick={handleButtonClick}
              hasBackgroundImage={!!state.backgroundImage}
              bgLocked={bgLocked}
              toggleBgLocked={() => setBgLocked(locked => !locked)}
              deleteImage={deleteImage}
              save={save}
              mirrorSelection={mirrorSelection}
              rotateSelection={rotateSelection}
              handleUndo={handleUndo}
              handleRedo={handleRedo}
              duplicateShapes={duplicateShapes}
              pasteShapes={pasteShapes}
              deleteSelectedShapes={deleteSelectedShapes}
              markAll={markAll}
              centerShapesHorizontally={centerShapesHorizontally}
              centerShapesVertically={centerShapesVertically}
              alignShapesInGrid={alignShapesInGrid}
              scaleShapesInX={scaleShapesInX}
              scaleShapesInY={scaleShapesInY}
              addItemsInGrid={addItemsInGrid}
              colorChange={colorChange}
              color={color}
              historyStep={historyStep}
              history={history}
              selectedIds={selectedIds}
              clipboard={clipboard}
              collapsed={collapsed}
              handleStraightenShapes={handleStraightenShapes}
              setBendMode={setBendMode}
            />
          )}
          {(!props.viewOnly &&
            <EslToolbar
              save={save}
              markAll={markAll}
              numbering={numbering}
              setNumbering={setNumbering}
              numberSeatsPerRow={numberSeatsPerRow}
              numberSeatsPerBlock={numberSeatsPerBlock}
              clearNumbering={clearNumbering}
              number={number}
              setNumber={setNumber}
              block={blockCounter}
              setBlocks={setBlocks}
              handleScanDirectionChange={handleScanDirectionChange}
              scanDirection={scanDirection}
              setScanDirection={setScanDirection}
              horizontalDirection={horizontalDirection}
              setHorizontalNumberingDirection={setHorizontalNumberingDirection}
              horizontalNumberingDirection={horizontalNumberingDirection}
              setVerticalNumberingDirection={setVerticalNumberingDirection}
              verticalNumberingDirection={verticalNumberingDirection}
              clearScanDirection={clearScanDirection}
              isManualSelecting={isManualSelecting}
              setIsManualSelecting={setIsManualSelecting}
              markAsSpecial={markAsSpecial}
              setHorizontalDirection={setHorizontalDirection}
            />)}
      </Flex>
    </>
);
});

export default Editor;
